import { MESSAGE_KINDS } from "../../modules/administration/plan-management/components/reminder-mapping";

/**
 * LF360-1896 — what a reminder says, and the values a picker offers.
 *
 * Kept apart from the components so the phrases can be tested directly: a pill
 * reading "30 min before Wake up" is the only thing an admin sees until they open
 * the picker, so it has to be right for every shape a reminder can take.
 *
 * The presets come from the design. They are deliberately NOT the full range: the
 * columns offer round values, while a reminder already stored at 09:25 keeps that
 * time and is offered as its own row. Snapping it to the nearest preset on open
 * would move a project's reminder without anyone asking for it.
 */

export const HOUR_VALUES = Array.from({ length: 24 }, (unused, hour) => hour);

export const MINUTE_PRESETS = [0, 10, 20, 30, 40, 50];

export const OFFSET_PRESETS = [0, 15, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720];

export const DIRECTIONS = { BEFORE: "before", AFTER: "after" };

/**
 * LF360-1881 — how far ahead of the assignment a reminder can be sent.
 *
 * Only a triggered task can hold one: it knows its date before that date
 * arrives, so "your task is coming on Friday" is a sentence there and nowhere
 * else. Stored as an amount and a unit rather than as minutes, which is what the
 * backend's ledger and `nextLeadTimeReminder` already work in.
 *
 * The pairs are offered as one column because the amount means nothing without
 * its unit — "3" alone could be three hours or three days, and the two are the
 * same row in the design's shape.
 */
// How far ahead of the assignment an announcement goes out: whole days, one to
// ninety, none of them missing.
//
// Days only, and every one of them. The hours this used to offer were the wrong
// unit for the question — an announcement is "a week before you are given this",
// not "six hours before" — and a list that jumped 3, 5, 7, 14, 30 made the ones
// it skipped unreachable, so an admin who wanted eleven days had nowhere to say
// so. The column scrolls; ninety rows cost nothing.
export const MAX_LEAD_DAYS = 90;

export const LEAD_PRESETS = Array.from(
  { length: MAX_LEAD_DAYS },
  (unused, index) => ({ value: index + 1, unit: "days" })
);

export const leadKey = (lead) =>
  `${Number(lead?.value) || 0}|${lead?.unit === "hours" ? "hours" : "days"}`;

export const formatLeadAmount = (value, unit) => {
  const amount = Number(value) || 0;
  const noun = unit === "hours" ? "hour" : "day";

  return `${amount} ${amount === 1 ? noun : `${noun}s`}`;
};

// The pairs a column shows: the presets, plus whatever the reminder already
// holds when that is not one of them. The same rule the clock columns follow —
// a stored value is never rounded into a preset behind the admin's back.
export const withCurrentLead = (presets, current) => {
  const value = Number(current?.value);

  if (!Number.isInteger(value) || value <= 0) return presets;

  const unit = current?.unit === "hours" ? "hours" : "days";

  if (presets.some((preset) => preset.value === value && preset.unit === unit)) {
    return presets;
  }

  return [...presets, { value, unit }].sort((a, b) => {
    const hours = (lead) => (lead.unit === "hours" ? lead.value : lead.value * 24);

    return hours(a) - hours(b);
  });
};

const pad = (value) => String(value).padStart(2, "0");

// "at 9:00" in the design — the hour is not padded, the minute is.
export const formatClock = (hour, minute) =>
  `${Number(hour) || 0}:${pad(Number(minute) || 0)}`;

export const formatOffsetAmount = (minutes) => {
  const total = Math.abs(Number(minutes) || 0);

  if (total === 0) return "In time";
  if (total < 60) return `${total} min`;

  const hours = Math.floor(total / 60);
  const rest = total % 60;

  // "1.5 hour" is the design's own label for ninety minutes, so halves are
  // spelled that way rather than as "1 hour 30 min" — the column and the phrase
  // on the pill have to say the same thing about the same reminder.
  if (rest === 30) return `${hours}.5 hour`;
  if (rest !== 0) return `${hours} hour ${rest} min`;

  return hours === 1 ? "1 hour" : `${hours} hours`;
};

export const directionOf = (minutes) =>
  (Number(minutes) || 0) < 0 ? DIRECTIONS.BEFORE : DIRECTIONS.AFTER;

export const signedOffset = (amount, direction) => {
  const total = Math.abs(Number(amount) || 0);

  return direction === DIRECTIONS.BEFORE ? -total : total;
};

const labelOfField = (fields, id) =>
  (fields || []).find((field) => field.value === id)?.label || null;

export const isLeadTime = (reminder) =>
  reminder?.messageKind === MESSAGE_KINDS.TASK_UPCOMING;

/**
 * The sentence on the pill.
 *
 * A relative reminder with no field yet says so rather than naming nothing: the
 * backend refuses one (EXTRA_REMINDER_TRIGGER_FIELD_REQUIRED), so the admin has to
 * be able to see which row is unfinished.
 */
export const describeReminder = (reminder, fields = []) => {
  if (!reminder) return "";

  if (isLeadTime(reminder)) {
    return `${formatLeadAmount(reminder.value, reminder.unit)} before, at ${formatClock(
      reminder.remindHour,
      reminder.remindMinute
    )}`;
  }

  if (reminder.notificationType !== "relative") {
    return `at ${formatClock(reminder.remindHour, reminder.remindMinute)}`;
  }

  const field = labelOfField(fields, reminder.triggerSubjectProfileFieldId);
  const anchor = field || "a time field";
  const minutes = Number(reminder.triggerOffsetMinutes) || 0;

  if (minutes === 0) return `at ${anchor}`;

  return `${formatOffsetAmount(minutes)} ${directionOf(minutes)} ${anchor}`;
};

/**
 * The same sentence, split where the design changes colour.
 *
 * The value carries the emphasis and the words around it are muted, so a list of
 * rows can be read down the amounts without the connecting words competing:
 * "**30 min** before Wake up".
 */
export const describeReminderParts = (reminder, fields = []) => {
  if (!reminder) return { strong: "", muted: "" };

  // "**2 days** before, at 9:00" — the amount carries the emphasis, as it does
  // on the other two kinds, and the day it counts back from is the assignment.
  if (isLeadTime(reminder)) {
    return {
      strong: formatLeadAmount(reminder.value, reminder.unit),
      muted: `before, at ${formatClock(reminder.remindHour, reminder.remindMinute)}`,
    };
  }

  if (reminder.notificationType !== "relative") {
    return {
      strong: `at ${formatClock(reminder.remindHour, reminder.remindMinute)}`,
      muted: "",
    };
  }

  const anchor = labelOfField(fields, reminder.triggerSubjectProfileFieldId) || "a time field";
  const minutes = Number(reminder.triggerOffsetMinutes) || 0;

  if (minutes === 0) return { strong: "at", muted: anchor };

  return {
    strong: formatOffsetAmount(minutes),
    muted: `${directionOf(minutes)} ${anchor}`,
  };
};

// The rows a column shows: the presets, plus whatever the reminder already holds
// when that is not one of them, in order.
export const withCurrentValue = (presets, current) => {
  const value = Number(current);

  if (!Number.isInteger(value) || presets.includes(value)) return presets;

  return [...presets, value].sort((a, b) => a - b);
};

/**
 * The time a newly added reminder starts out at.
 *
 * The first free slot from nine in the morning onwards, wrapping to the small
 * hours only if the rest of the day is spoken for.
 *
 * Not "an hour after the last one", which is where this started: the list is
 * ordered by time, so that is always the bottom row — on a project with several
 * reminders the one just added is off the end of the list and has to be found
 * before it can be changed. And not the first free slot of the day either, which
 * fixed that but read oddly: the first reminder a project gets is nine o'clock,
 * and the second would then be midnight.
 *
 * Nine is where a reminder someone might actually keep starts, so new ones gather
 * around it and sort next to each other.
 *
 * Slots come from the same presets the picker offers, so the time a new row lands
 * on is always one the columns can show as selected. Only fixed times on the
 * task's own day occupy a slot: an offset from a profile answer has no place on
 * the clock, and a reminder sent days before the assignment is not on that day at
 * all — the backend counts the two as different reminders even at the same time.
 */
export const DEFAULT_REMINDER_HOUR = 9;

export const nextFreeSlot = (reminders = [], { minMinutes = null } = {}) => {
  const taken = new Set(
    (reminders || [])
      .filter(
        (reminder) =>
          reminder && reminder.notificationType !== "relative" && !isLeadTime(reminder)
      )
      .map(
        (reminder) =>
          (Number(reminder.remindHour) || 0) * 60 + (Number(reminder.remindMinute) || 0)
      )
  );

  const hours = [
    ...HOUR_VALUES.slice(DEFAULT_REMINDER_HOUR),
    ...HOUR_VALUES.slice(0, DEFAULT_REMINDER_HOUR),
  ];

  for (const hour of hours) {
    for (const minute of MINUTE_PRESETS) {
      const slot = hour * 60 + minute;

      // LF360-1940: on a task being assigned for today, a time that has already
      // passed is refused on save. Adding a reminder must not hand the admin one
      // to be refused for.
      if (Number.isInteger(minMinutes) && slot < minMinutes) { continue; }

      if (!taken.has(slot)) return slot;
    }
  }

  // Every slot the picker offers is spoken for; nothing sensible to add.
  return null;
};

/**
 * A free offset for a reminder about to start counting from `fieldId`.
 *
 * The mirror of nextFreeSlot, for the other direction. Two offsets from the same
 * profile answer are the same reminder, so becoming relative can land on one
 * that is already taken — and refusing that is refusing a change of kind, which
 * the control cannot show. It moves to the next unused offset instead, the way a
 * reminder returning to a time of day moves to the next free hour.
 *
 * Signed, because before and after the answer are different reminders: the
 * search walks the presets outward from zero in both directions.
 */
export const nextFreeOffset = (reminders = [], fieldId) => {
  const taken = new Set(
    (reminders || [])
      .filter(
        (reminder) =>
          reminder
          && reminder.notificationType === "relative"
          && !isLeadTime(reminder)
          && (reminder.triggerSubjectProfileFieldId ?? null) === (fieldId ?? null)
      )
      .map((reminder) => Number(reminder.triggerOffsetMinutes) || 0)
  );

  for (const preset of OFFSET_PRESETS) {
    if (preset === 0) {
      if (!taken.has(0)) return 0;
      continue;
    }

    if (!taken.has(-preset)) return -preset;
    if (!taken.has(preset)) return preset;
  }

  // Every offset the picker offers, both ways, is spoken for.
  return null;
};

/**
 * A free time of day for an announcement this far ahead.
 *
 * Announcements collide only with each other, and only at the same amount — so
 * the search is over the ones sharing that amount, and the thing that moves is
 * the hour. It has to move, because a reminder arriving here often has no hour
 * of its own to keep: a relative one is stored with none, so it lands on the
 * same nine o'clock every time and collides with whatever is already there.
 * That was the whole of "I cannot get to Before from Relative".
 */
export const nextFreeLeadSlot = (reminders = [], value, unit) => {
  const amount = Number(value) || 0;
  const measure = unit === "hours" ? "hours" : "days";

  const taken = new Set(
    (reminders || [])
      .filter(
        (reminder) =>
          isLeadTime(reminder)
          && (Number(reminder.value) || 0) === amount
          && (reminder.unit === "hours" ? "hours" : "days") === measure
      )
      .map(
        (reminder) =>
          (Number(reminder.remindHour) || 0) * 60 + (Number(reminder.remindMinute) || 0)
      )
  );

  const hours = [
    ...HOUR_VALUES.slice(DEFAULT_REMINDER_HOUR),
    ...HOUR_VALUES.slice(0, DEFAULT_REMINDER_HOUR),
  ];

  for (const hour of hours) {
    for (const minute of MINUTE_PRESETS) {
      const slot = hour * 60 + minute;

      if (!taken.has(slot)) return slot;
    }
  }

  return null;
};
